<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" lg="10" offset-lg="1">
        <h2 class="title mb-3">問い合わせ一覧</h2>
        <permission-guard situation="contact_management">
          <v-container fluid class="px-0">
            <v-layout row>
              <v-text-field
                v-model="searchQuery.subject_or_content_cont"
                label="キーワード"
                style="max-width: 200px"
                class="ma-2"
              />
              <v-text-field
                v-model="searchQuery.category_cont"
                label="カテゴリ"
                style="max-width: 150px"
                class="ma-2"
              />
              <v-select
                v-model="searchQuery.status_eq"
                :items="statuses"
                item-text="type"
                style="max-width: 120px"
                class="ma-2"
                item-value="value"
                clearable
                label="ステータス"
              />
              <v-btn
                color="primary"
                dark
                tile
                outlined
                class="ma-2"
                @click="searchContacts"
              >
                検索
              </v-btn>
              <router-link
                :to="{ name: 'adminContactsEdit' }"
                class="contactEditLink"
              >
                <v-icon color="grey">mdi-cog</v-icon>
              </router-link>
            </v-layout>
            <v-simple-table fixed-header>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-center" style="width: 50px">ID</th>
                    <th />
                    <th class="text-left">カテゴリ</th>
                    <th class="text-left">件名</th>
                    <th class="text-left" style="width: 300px">内容</th>
                    <th class="text-left">作成日</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in contacts" :key="c.id">
                    <td class="text-center">
                      {{ c.id }}
                    </td>
                    <td>
                      <v-chip
                        v-if="c.status == 'unsupported'"
                        color="error"
                        x-small
                        label
                      >
                        <strong>未対応</strong>
                      </v-chip>
                      <v-chip v-if="c.status == 'fixed'" x-small label>
                        <strong>対応済</strong>
                      </v-chip>
                    </td>
                    <td class="text-left">
                      {{ c.category }}
                    </td>
                    <td>
                      {{ c.subject | subjectPlaceholder }}
                    </td>
                    <td class="pb-5 px-4 ">
                      <p class="indexContact">{{ c.content | truncateText }}</p>
                    </td>
                    <td>
                      {{ c.createdAt | moment }}
                    </td>
                    <td class="text-center">
                      <v-btn
                        small
                        outlined
                        color="primary"
                        @click="showContact(c.id)"
                      >
                        詳細
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <infinite-loading
              spinner="spiral"
              class="mt-2"
              :identifier="infiniteId"
              @infinite="infiniteHandler"
            >
              <template v-slot:no-more>
                <v-chip label outlined> 全て表示されました。 </v-chip>
              </template>
              <template v-slot:no-results>
                <v-chip label outlined> 表示できるものがありません。 </v-chip>
              </template>
            </infinite-loading>
          </v-container>
        </permission-guard>
      </v-col>
    </v-row>

    <v-dialog v-if="contact" v-model="showDialog" width="500">
      <v-card>
        <v-card-title class="mb-2">
          {{ contact.subject | subjectPlaceholder }}
        </v-card-title>
        <v-card-subtitle class="caption">
          {{ contact.category }}
        </v-card-subtitle>
        <v-card-text>
          <h3 class="overline font-weight-bold">ステータス</h3>
          <p>
            <v-chip
              v-if="contact.status == 'unsupported'"
              color="error"
              small
              outlined
            >
              未対応
            </v-chip>
            <v-chip v-if="contact.status == 'fixed'" small> 対応済 </v-chip>
          </p>
          <h3 class="overline font-weight-bold">お名前</h3>
          <p>
            {{ contact.name }}
          </p>
          <h3 class="overline font-weight-bold">メールアドレス</h3>
          <p>
            {{ contact.email }}
          </p>
          <div v-if="contact.company">
            <h3 class="overline font-weight-bold">会社名</h3>
            <p>
              {{ contact.company }}
            </p>
          </div>

          <h3 class="overline font-weight-bold">内容</h3>
          <p v-text="contact.content" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            v-if="contact.status == 'unsupported'"
            color="primary"
            outlined
            tile
            class="ma-3"
            @click="fixContact"
          >
            対応済みにする
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import PermissionGuard from '@/components/commons/PermissionGuard'
import InfiniteLoading from 'vue-infinite-loading'
import headUtilsMixin from '@/mixins/head_utils'
import Qs from 'qs'
import moment from 'moment'

export default {
  components: {
    PermissionGuard,
    InfiniteLoading
  },
  filters: {
    moment: function (val) {
      if (val) {
        return moment(val).format('YYYY年M月D日 HH時mm分')
      }
    },
    truncateText: function (val) {
      if (val.length > 130) {
        return val.slice(0, 130) + '…'
      } else {
        return val
      }
    },
    subjectPlaceholder: function (val) {
      if (!val) {
        return '（件名なし）'
      } else {
        return val
      }
    }
  },
  mixins: [headUtilsMixin],
  data: () => {
    return {
      pageTitle: '問い合わせ一覧',
      page: 1,
      contacts: [],
      contact: null,
      searchQuery: {
        category_cont: null,
        subject_or_content_cont: null,
        status_eq: null
      },
      infiniteId: +new Date(),
      showDialog: false,
      statuses: [
        { type: '未対応', value: 0 },
        { type: '対応済', value: 1 }
      ]
    }
  },
  methods: {
    searchContacts () {
      this.page = 1
      this.contacts = []
      this.infiniteId++
    },
    showContact (id) {
      this.showDialog = true
      this.contact = { ...this.contacts.find((c) => c.id === id) }
    },
    infiniteHandler ($state) {
      axios
        .get('/api/contacts', {
          params: {
            page: this.page,
            q: this.searchQuery
          },
          paramsSerializer: function (params) {
            return Qs.stringify(params, { arrayFormat: 'brackets' })
          }
        })
        .then(({ data }) => {
          const fetchedContacts = data.data
          if (fetchedContacts.length) {
            this.page++
            for (const i in fetchedContacts) {
              const contact = fetchedContacts[i].attributes
              this.contacts.push(contact)
            }
            $state.loaded()
          } else {
            $state.complete()
          }
        })
    },
    fixContact () {
      const query = {
        contact: {
          status: 'fixed'
        }
      }
      axios.patch(`/api/contacts/${this.contact.id}`, query).then(
        ({ data }) => {
          const updatedContact = data.data.attributes
          const contact = this.contacts.find((c) => c.id === updatedContact.id)
          Object.assign(contact, updatedContact)
          this.showDialog = false
          const payload = {
            status: 200,
            message: '問い合わせを対応済にしました'
          }
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
        },
        (error) => {
          const payload = {
            status: error.response.status,
            message: error.response.data.errors.toString()
          }
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
        }
      )
    }
  }
}
</script>

<style scoped>
th,
td {
  vertical-align: middle;
}

.indexContact {
  width: 500px;
  padding: 10px;
  white-space: pre-line;
  word-wrap: break-word;
}
.contactEditLink {
  margin: 8px 60px 8px auto;
}
</style>
